<template>
    <div class="sfc-form">
        <!-- Field order per spec 2026-10-13 §5.2: name TH, name EN, slug, descriptions, cover, items, active. -->
        <div class="form-group" :class="cls('name_th')">
            <label class="form-control-label">{{ t("f.name_th") }} <span class="text-danger">*</span></label>
            <el-input v-model="form.name_th" :maxlength="120" show-word-limit :disabled="disabled" data-testid="sfc-name-th"></el-input>
            <small v-if="errors.name_th" class="text-danger">{{ rule(errors.name_th) }}</small>
        </div>
        <div class="form-group" :class="cls('name_en')">
            <label class="form-control-label">{{ t("f.name_en") }} <span class="text-danger">*</span></label>
            <el-input v-model="form.name_en" :maxlength="120" show-word-limit :disabled="disabled" data-testid="sfc-name-en" @input="onNameEn"></el-input>
            <small v-if="errors.name_en" class="text-danger">{{ rule(errors.name_en) }}</small>
        </div>
        <div v-if="!discovery" class="form-group" :class="cls('slug')">
            <label class="form-control-label">{{ t("f.slug") }} <span class="text-danger">*</span></label>
            <el-input v-model="form.slug" :maxlength="60" :disabled="disabled" data-testid="sfc-slug" @input="slugTouched = true">
                <template slot="prepend">/collections/</template>
            </el-input>
            <small v-if="errors.slug" class="text-danger d-block" data-testid="sfc-slug-error">{{ slugError(errors.slug) }}</small>
            <small class="text-muted d-block">{{ t("slug_hint") }}</small>
        </div>
        <div class="form-group" :class="cls('description_th')">
            <label class="form-control-label">{{ t("f.description_th") }}</label>
            <el-input v-model="form.description_th" type="textarea" :rows="2" :maxlength="500" show-word-limit :disabled="disabled"></el-input>
            <small v-if="errors.description_th" class="text-danger">{{ rule(errors.description_th) }}</small>
        </div>
        <div class="form-group" :class="cls('description_en')">
            <label class="form-control-label">{{ t("f.description_en") }}</label>
            <el-input v-model="form.description_en" type="textarea" :rows="2" :maxlength="500" show-word-limit :disabled="disabled"></el-input>
            <small v-if="errors.description_en" class="text-danger">{{ rule(errors.description_en) }}</small>
        </div>
        <div class="form-group">
            <label class="form-control-label">{{ t("f.cover") }}</label>
            <s3-image-upload object-type="storefront" :disabled="disabled" :value="form.cover_url || ''" @input="form.cover_url = $event" @update:objectKey="form.cover_key = $event || ''"></s3-image-upload>
        </div>

        <!-- One ordered list fed by two pickers (products + bundles). -->
        <div class="form-group" :class="cls('items')">
            <label class="form-control-label">
                {{ t("f.items") }}
                <span class="text-muted" data-testid="sfc-counter">({{ $t("storefront_collection.items_counter", { count: form.items.length, max: maxItems }) }})</span>
            </label>
            <div class="row">
                <div class="col-md-7 mb-2">
                    <el-select
                        :value="null"
                        class="d-block"
                        filterable
                        remote
                        reserve-keyword
                        :disabled="disabled || full"
                        :placeholder="t('f.search_product')"
                        :remote-method="searchProducts"
                        :loading="searching"
                        data-testid="sfc-product-select"
                        @change="(id) => add('product', id)"
                        @visible-change="(open) => open && !productResults.length && !searching && searchProducts('')"
                    >
                        <el-option v-for="p in productResults" :key="'p' + p.id" :label="productLabel(p)" :value="p.id">
                            <span class="d-flex align-items-center">
                                <img v-if="p.image_url" :src="p.image_url" class="sfc-thumb mr-2" alt="" />
                                <span>{{ productLabel(p) }}</span>
                            </span>
                        </el-option>
                    </el-select>
                </div>
                <div class="col-md-5 mb-2">
                    <el-select
                        :value="null"
                        class="d-block"
                        filterable
                        :disabled="disabled || full"
                        :placeholder="t('f.pick_bundle')"
                        data-testid="sfc-bundle-select"
                        @change="(id) => add('bundle', id)"
                        @visible-change="loadBundles"
                    >
                        <el-option v-for="b in bundles" :key="'b' + b.id" :label="bundleLabel(b)" :value="b.id"></el-option>
                    </el-select>
                </div>
            </div>
            <small v-if="errors.items" class="text-danger d-block">{{ rule(errors.items) }}</small>
            <small class="text-muted d-block mb-1">{{ t("items_hint") }}</small>

            <p v-if="!form.items.length" class="text-muted small mb-0">{{ t("no_items") }}</p>
            <ul v-else class="list-unstyled mb-0" data-testid="sfc-items">
                <li v-for="(it, i) in form.items" :key="it.type + it.id" class="d-flex align-items-center py-1 border-bottom sfc-row" :data-item="it.type + ':' + it.id">
                    <el-tag size="mini" :type="it.type === 'bundle' ? 'warning' : ''" class="mr-2 sfc-type">{{ t(it.type === "bundle" ? "type_bundle" : "type_product") }}</el-tag>
                    <img v-if="thumb(it)" :src="thumb(it)" class="sfc-thumb mr-2" alt="" />
                    <span v-else class="sfc-thumb sfc-thumb--empty mr-2"></span>
                    <span class="flex-fill small text-truncate">{{ itemLabel(it) }}</span>
                    <el-tag v-if="isDead(it)" type="danger" size="mini" effect="dark" class="mr-2" data-testid="sfc-unavailable">{{ t("unavailable") }}</el-tag>
                    <template v-if="!disabled">
                        <base-button size="sm" type="neutral" class="p-1" :disabled="i === 0" :title="t('move_up')" :aria-label="t('move_up')" @click="move(i, -1)"><i class="fa fa-arrow-up"></i></base-button>
                        <base-button size="sm" type="neutral" class="p-1" :disabled="i === form.items.length - 1" :title="t('move_down')" :aria-label="t('move_down')" @click="move(i, 1)"><i class="fa fa-arrow-down"></i></base-button>
                        <base-button size="sm" type="danger" class="p-1" :title="t('remove')" :aria-label="t('remove')" @click="remove(i)"><i class="fa fa-times"></i></base-button>
                    </template>
                </li>
            </ul>
        </div>

        <div class="form-group mb-0">
            <el-switch v-model="form.is_active" :disabled="disabled" data-testid="sfc-active"></el-switch>
            <span class="ml-2">{{ t("f.is_active") }}</span>
        </div>
    </div>
</template>

<script>
import { Input, Select, Option, Tag, Switch } from "element-ui";
import S3ImageUpload from "@/components/Inputs/S3ImageUpload.vue";
import { LandingPageService } from "@/resource";
import { MAX_ITEMS, addItem, isUnavailable, moveItem, slugify } from "@/utils/storefrontCollection";

const normalizeProduct = (p) => ({ id: p.id, code: p.code || "", name: p.name || p.name_th || p.name_en || "", image_url: p.image_url || "" });

// Shared editor for one collection (dialog) and the Discovery Sets singleton
// (inline tab, `discovery` hides the slug). Mutates the `form` object it is
// given, like LpSectionForm does with its section. Product search reuses the
// LP picker endpoints (LandingPageService.searchProducts / getProduct); bundles
// come from LandingPageService.listPromotions (AppCatalogPromotionBundleProvider).
export default {
    name: "StorefrontCollectionForm",
    components: {
        S3ImageUpload,
        [Input.name]: Input,
        [Select.name]: Select,
        [Option.name]: Option,
        [Tag.name]: Tag,
        [Switch.name]: Switch
    },
    props: {
        form: { type: Object, required: true },
        discovery: { type: Boolean, default: false },
        disabled: { type: Boolean, default: false },
        errors: { type: Object, default: () => ({}) },
        unavailable: { type: Object, default: () => ({ products: [], bundles: [] }) }
    },
    data() {
        return {
            maxItems: MAX_ITEMS,
            // a new collection suggests its slug from the EN name until the slug is typed in
            slugTouched: !!this.form.slug,
            searching: false,
            productResults: [],
            knownProducts: {},
            bundles: [],
            bundlesLoaded: false
        };
    },
    computed: {
        full() {
            return this.form.items.length >= MAX_ITEMS;
        },
        bundleById() {
            const m = {};
            this.bundles.forEach((b) => { m[b.id] = b; });
            return m;
        }
    },
    watch: {
        "form.items": { immediate: true, handler: "seed" }
    },
    methods: {
        t(k) {
            return this.$t("storefront_collection." + k);
        },
        rule(r) {
            const key = "storefront_collection.rule." + r;
            return this.$te(key) ? this.$t(key) : this.$t("storefront_collection.rule.invalid");
        },
        slugError(r) {
            if (r === "taken") return this.t("err.slug_taken");
            if (r === "reserved") return this.t("err.slug_reserved");
            return this.rule(r);
        },
        cls(f) {
            return this.errors[f] ? "has-danger" : "";
        },
        onNameEn(v) {
            if (!this.discovery && !this.form.id && !this.slugTouched) this.form.slug = slugify(v);
        },
        productLabel(p) {
            return `${p.code || p.id} - ${p.name || ""}`;
        },
        bundleLabel(b) {
            return `#${b.id} ${b.name_th || b.name || b.name_en || ""}`;
        },
        itemLabel(it) {
            if (it.type === "bundle") {
                const b = this.bundleById[it.id];
                return b ? this.bundleLabel(b) : "#" + it.id;
            }
            const p = this.knownProducts[it.id];
            return p ? this.productLabel(p) : "#" + it.id;
        },
        thumb(it) {
            if (it.type === "bundle") {
                const b = this.bundleById[it.id];
                return (b && (b.cover_url || b.image_url)) || "";
            }
            const p = this.knownProducts[it.id];
            return (p && p.image_url) || "";
        },
        isDead(it) {
            return isUnavailable(it, this.unavailable);
        },
        add(type, id) {
            if (!id) return;
            if (type === "product") {
                const p = this.productResults.find((x) => x.id === id);
                if (p) this.$set(this.knownProducts, id, p);
            }
            this.form.items = addItem(this.form.items, type, id);
        },
        move(i, dir) {
            this.form.items = moveItem(this.form.items, i, dir);
        },
        remove(i) {
            const a = this.form.items.slice();
            a.splice(i, 1);
            this.form.items = a;
        },
        async seed(items) {
            const list = Array.isArray(items) ? items : [];
            if (list.some((it) => it.type === "bundle")) this.loadBundles(true);
            const missing = list.filter((it) => it.type === "product" && !this.knownProducts[it.id]).map((it) => it.id);
            await Promise.all(missing.map((id) => LandingPageService.getProduct(id).then(
                (r) => {
                    const p = (r.data && r.data.data) || r.data;
                    if (p && p.id) this.$set(this.knownProducts, p.id, normalizeProduct(p));
                },
                () => null
            )));
        },
        async searchProducts(query) {
            this.searching = true;
            try {
                const params = { page: 1, limit: 30, app_enable: 1 };
                if (query) params.keyword = query;
                const res = await LandingPageService.searchProducts(params);
                this.productResults = ((res.data && res.data.data) || []).map(normalizeProduct);
            } catch (e) {
                this.productResults = [];
            } finally {
                this.searching = false;
            }
        },
        async loadBundles(open) {
            if (!open || this.bundlesLoaded) return;
            this.bundlesLoaded = true;
            try {
                const res = await LandingPageService.listPromotions({ page: 1, limit: 100 });
                const body = res.data || {};
                this.bundles = body.data || body.items || [];
            } catch (e) {
                this.bundlesLoaded = false;
                this.bundles = [];
            }
        }
    }
};
</script>

<style scoped>
.sfc-thumb {
    width: 28px;
    height: 28px;
    object-fit: cover;
    border-radius: 4px;
    flex-shrink: 0;
}
.sfc-thumb--empty {
    display: inline-block;
    background: #e9ecef;
}
.sfc-type {
    min-width: 64px;
    text-align: center;
}
</style>
